<template>
  <div ref="turnstile"></div>
</template>

<script>
import {eventBus} from '@/store';

const turnstileSrc = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const turnstileLoadFunction = 'cfTurnstileOnLoad';

let turnstileState = typeof window !== 'undefined' ? (window.turnstile !== undefined ? 'ready' : 'unloaded') : 'unloaded';
let turnstileLoad = {
  resolve: (value) => {},
  reject: (value) => {},
};

export default {
  name: 'VueTurnstile',

  emits: ['update', 'error', 'unsupported'],

  props: {
    siteKey: {
      type: String,
      required: true,
    },
    resetInterval: {
      type: Number,
      required: false,
      default: 295 * 1000,
    },
    size: {
      type: String,
      required: false,
      default: 'normal',
    },
    theme: {
      type: String,
      required: false,
      default: 'auto',
    },
    language: {
      type: String,
      required: false,
      default: 'auto',
    },
    action: {
      type: String,
      required: false,
      default: '',
    },
    appearance: {
      type: String,
      required: false,
      default: 'always',
    },
    renderOnMount: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  data() {
    return {
      resetTimeout: undefined,
      widgetId: undefined,
    };
  },

  computed: {
    turnstileOptions() {
      return {
        sitekey: this.siteKey,
        theme: this.theme,
        language: this.language,
        size: this.size,
        callback: this.callback,
        action: this.action,
        appearance: this.appearance,
        'error-callback': this.errorCallback,
        'unsupported-callback': this.unsupportedCallback,
      };
    },
  },

  methods: {
    unsupportedCallback() {
      this.$emit('unsupported');
    },

    errorCallback(code) {
      this.$emit('error', code);
    },

    callback(token) {
      this.$emit('update', token);
      this.startResetTimeout();
    },

    reset() {
      if (window.turnstile) {
        this.$emit('update', '');
        window.turnstile.reset();
      }
    },

    remove() {
      if (this.widgetId) {
        window.turnstile.remove(this.widgetId);
        this.widgetId = undefined;
      }
    },

    render() {
      this.widgetId = window.turnstile.render(this.$refs.turnstile, this.turnstileOptions);
    },

    startResetTimeout() {
      this.resetTimeout = setTimeout(() => {
        this.reset();
      }, this.resetInterval);
    },
  },

  async mounted() {
    const turnstileLoadPromise = new Promise((resolve, reject) => {
      turnstileLoad = { resolve, reject };
      if (turnstileState === 'ready') resolve(undefined);
    });

    window[turnstileLoadFunction] = () => {
      turnstileLoad.resolve();
      turnstileState = 'ready';
    };

    const ensureTurnstile = () => {
      if (turnstileState === 'unloaded') {
        turnstileState = 'loading';
        const url = `${turnstileSrc}?onload=${turnstileLoadFunction}&render=explicit`;
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.addEventListener('error', () => {
          turnstileLoad.reject('Failed to load Turnstile.');
        });
        document.head.appendChild(script);
      }
      return turnstileLoadPromise;
    };

    await ensureTurnstile();

    if (this.renderOnMount) {
      this.render();
    }

    eventBus.$on('reset-turnstile', this.reset);
  },

  beforeUnmount() {
    this.remove();
    clearTimeout(this.resetTimeout);
    eventBus.$off('reset-turnstile', this.reset);
  }
};
</script>
